/**
 * Mini App Entry Point
 * Hotel Booking LIFF Application
 */

import './styles.css';
import 'litepicker/dist/css/litepicker.css';

import { initLiff, isLoggedIn, login, getUserProfile, setUserProfile, isInLineApp } from './liff';
import { setLanguage, detectLanguage, t, getCurrentLanguage } from './i18n';
import { initTenantFromUrl, ensureTenantResolved, fetchTenantConfig, Language } from './config';
import { getUserProfile as getApiUserProfile } from './api';
import {
  getState,
  setState,
  subscribe,
  setUser,
  setLoading,
  setError,
  navigateTo,
  setGuestInfo,
  loadSavedGuestInfo,
  saveGuestInfoToStorage,
} from './store';
import { LoadingSpinner, ErrorMessage, LanguageModal, renderServiceTabs } from './components';
import {
  renderSearchPage,
  initSearchPage,
  renderRoomsPage,
  initRoomsPage,
  renderGuestInfoPage,
  initGuestInfoPage,
  loadUserProfileAsync,
  renderConfirmPage,
  initConfirmPage,
  renderPaymentPage,
  initPaymentPage,
  renderSuccessPage,
  initSuccessPage,
  renderMyBookingsPage,
  initMyBookingsPage,
  renderRoomTypesPage,
  initRoomTypesPage,
  renderSpaPage,
  initSpaPage,
  renderRestaurantPage,
  initRestaurantPage,
  renderMicePage,
  initMicePage,
  renderRoomServicePage,
  initRoomServicePage,
  saveRoomServiceInputsFromDom,
  renderOnboardPage,
  initOnboardPage,
  fetchFreeTierStatus,
} from './pages';
import { renderEngine, initEngine } from './engines';
import { getConfigServices, getCachedMiniAppConfig } from './miniapp-config';

// App container
const app = document.getElementById('app')!;

/**
 * Render the current page based on state
 */
function render(): void {
  const state = getState();

  // Show loading
  if (state.loading) {
    app.innerHTML = LoadingSpinner();
    return;
  }

  // Show error with retry
  if (state.error) {
    app.innerHTML = ErrorMessage(state.error, () => {
      setError(null);
      render();
    });

    // Set up retry handler
    window.handleRetry = () => {
      setError(null);
      render();
    };
    return;
  }

  // Render current page
  switch (state.currentPage) {
    case 'search':
      app.innerHTML = renderSearchPage();
      initSearchPage();
      break;

    case 'rooms':
      app.innerHTML = renderRoomsPage();
      initRoomsPage();
      break;

    case 'guest-info':
      // Load user profile from API before rendering (async)
      loadUserProfileAsync().finally(() => {
        if (getState().currentPage !== 'guest-info') return;
        app.innerHTML = renderGuestInfoPage();
        initGuestInfoPage();
      });
      // Show loading while fetching profile
      app.innerHTML = LoadingSpinner();
      return; // Don't continue, will re-render after profile is loaded

    case 'confirm':
      app.innerHTML = renderConfirmPage();
      initConfirmPage();
      break;

    case 'payment':
      app.innerHTML = renderPaymentPage();
      initPaymentPage();
      break;

    case 'success':
      app.innerHTML = renderSuccessPage();
      initSuccessPage();
      break;

    case 'my-bookings':
      app.innerHTML = renderMyBookingsPage();
      initMyBookingsPage();
      break;

    case 'room-types':
      app.innerHTML = renderRoomTypesPage();
      initRoomTypesPage();
      break;

    case 'spa':
      app.innerHTML = renderSpaPage();
      initSpaPage();
      break;

    case 'restaurant':
      app.innerHTML = renderRestaurantPage();
      initRestaurantPage();
      break;

    case 'mice':
      app.innerHTML = renderMicePage();
      initMicePage();
      break;

    case 'room-service':
      app.innerHTML = renderRoomServicePage();
      initRoomServicePage();
      break;

    case 'onboard':
      app.innerHTML = renderOnboardPage();
      initOnboardPage();
      break;

    case 'service':
      {
        const currentServiceId = getState().activeServiceId || '';
        const services = getConfigServices();
        const activeSvc = services.find((s: any) => s.id === currentServiceId) || services[0];
        if (activeSvc) {
          app.innerHTML = renderEngine(activeSvc);
          initEngine(activeSvc);
        } else {
          app.innerHTML = `
            ${renderServiceTabs('service', currentServiceId)}
            <div style="min-height: calc(100vh - 120px); display:flex; flex-direction:column; align-items:center; justify-content:center; padding:32px 20px; text-align:center; background:#f9fafb;">
              <div style="font-size:48px; margin-bottom:16px;">✨</div>
              <h2 style="font-size:20px; font-weight:700; color:#1f2937; margin-bottom:8px;">${t('appName')}</h2>
              <div style="display:inline-block; padding:4px 12px; background:#e0f2fe; color:#0369a1; border-radius:16px; font-size:12px; font-weight:600; margin-bottom:12px;">${t('comingSoon')}</div>
              <p style="font-size:14px; color:#6b7280; max-width:320px; line-height:1.5;">${t('serviceComingSoonDesc')}</p>
            </div>
          `;
        }
      }
      break;

    default:
      app.innerHTML = renderSearchPage();
      initSearchPage();
  }
  (window as any).renderApp = render;
  injectLangModal();
  scrollActiveServiceTabIntoView();
}

/**
 * Scroll active service tab into visible viewport smoothly
 */
function scrollActiveServiceTabIntoView(): void {
  requestAnimationFrame(() => {
    const activeTab = document.querySelector('.nav-service-tab.active');
    if (activeTab && typeof (activeTab as HTMLElement).scrollIntoView === 'function') {
      (activeTab as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  });
}

/**
 * Injects or updates the LanguageModal in document.body
 */
function injectLangModal(): void {
  let modalContainer = document.getElementById('lang-modal-container');
  if (!modalContainer) {
    modalContainer = document.createElement('div');
    modalContainer.id = 'lang-modal-container';
    document.body.appendChild(modalContainer);
  }
  // Check if modal is currently open to preserve open state during re-render
  const wasOpen = document.getElementById('lang-modal-overlay')?.classList.contains('open');
  modalContainer.innerHTML = LanguageModal();
  if (wasOpen) {
    document.getElementById('lang-modal-overlay')?.classList.add('open');
  }
}

/**
 * Initialize the application
 */
async function initApp(): Promise<void> {
  setLoading(true);

  try {
    // Helper to get URL search params safely, including decoding LINE LIFF liff.state
    const getCombinedUrlParams = (): URLSearchParams => {
      const currentSearch = window.location.search || '';
      const params = new URLSearchParams(currentSearch);

      // LINE LIFF passes query params via liff.state when redirecting after login
      const liffState = params.get('liff.state');
      if (liffState) {
        try {
          const decodedState = decodeURIComponent(liffState);
          const stateQuery = decodedState.startsWith('?') ? decodedState.slice(1) : (decodedState.includes('?') ? decodedState.split('?')[1] : decodedState);
          const stateParams = new URLSearchParams(stateQuery);
          stateParams.forEach((value, key) => {
            if (!params.has(key) || key === 'page' || key === 'tab' || key === 'room' || key === 'roomNumber' || key === 'lang' || key === 'tenant' || key === 'tenantId' || key === 'table' || key === 'outlet' || key === 'outletId' || key === 'mode') {
              params.set(key, value);
            }
          });
        } catch (e) {
          console.warn('[MiniApp] Failed to parse liff.state:', e);
        }
      }

      // Also check hash query params if any (e.g. #/?page=my-bookings)
      if (window.location.hash && window.location.hash.includes('?')) {
        try {
          const hashQuery = window.location.hash.split('?')[1];
          const hashParams = new URLSearchParams(hashQuery);
          hashParams.forEach((value, key) => {
            if (!params.has(key) || key === 'page' || key === 'tab' || key === 'room' || key === 'roomNumber' || key === 'lang' || key === 'tenant' || key === 'tenantId' || key === 'table' || key === 'outlet' || key === 'outletId' || key === 'mode') {
              params.set(key, value);
            }
          });
        } catch (_) {}
      }

      return params;
    };

    const urlParams = getCombinedUrlParams();

    // Resolve tenant ID from URL ?tenant= param (or fallback to LIFF resolution / env)
    const resolvedTenant = await ensureTenantResolved();
    console.log('[MiniApp] Tenant resolved:', resolvedTenant);

    if (!resolvedTenant) {
      setLoading(false);
      const root = document.getElementById('app');
      if (root) {
        root.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;background:#f9f9f9;">
            <div style="font-size:64px;margin-bottom:16px;">⚠️</div>
            <h2 style="color:#cf1322;margin-bottom:8px;font-size:20px;">
              ไม่พบข้อมูลโรงแรม
            </h2>
            <p style="color:#666;margin-bottom:24px;font-size:14px;">กรุณาเข้าใช้งานผ่านลิงก์ของโรงแรมโดยตรง</p>
          </div>`;
      }
      return;
    }

    // Page & Room parameters (extracted early for omnichannel routing decisions)
    const page = (urlParams.get('page') || '').toLowerCase().trim();
    const tabParam = (urlParams.get('tab') || '').toLowerCase().replace(/[\s_-]/g, '');
    const roomParam = (urlParams.get('room') || urlParams.get('roomNumber') || '').trim();
    const tableParam = (urlParams.get('table') || '').trim();
    const outletParam = (urlParams.get('outlet') || urlParams.get('outletId') || '').trim();

    if (roomParam) {
      try { localStorage.setItem('_guest_room_number', roomParam); } catch (_) {}
    }
    // Persist table + outlet so they survive WeChat/LINE OAuth redirects that strip URL params
    if (tableParam) {
      try {
        localStorage.setItem('_guest_table_number', tableParam);
        sessionStorage.setItem('_guest_table_number', tableParam);
      } catch (_) {}
    }
    if (outletParam) {
      try {
        localStorage.setItem('_guest_outlet_id', outletParam);
        sessionStorage.setItem('_guest_outlet_id', outletParam);
      } catch (_) {}
    }

    const isRoomServicePage =
      page === 'room-service' ||
      page === 'hk' ||
      page === 'housekeeping' ||
      page === 'engineer' ||
      page === 'maintenance' ||
      page === 'repair' ||
      page === 'my-requests' ||
      page === 'my-request';

    // Fetch Tenant App Config (runtime supportedLanguages, defaultLanguage, features)
    const tenantConfig = await fetchTenantConfig().catch(() => null);
    const tenantSupportedLangs = tenantConfig?.supportedLanguages && Array.isArray(tenantConfig.supportedLanguages) && tenantConfig.supportedLanguages.length > 0
      ? tenantConfig.supportedLanguages
      : ['th', 'en', 'zh', 'ru', 'ja', 'ko'];
    const tenantDefaultLang = (tenantConfig?.defaultLanguage && tenantSupportedLangs.includes(tenantConfig.defaultLanguage))
      ? tenantConfig.defaultLanguage
      : (tenantSupportedLangs[0] || 'th');

    // Language parameters
    const paramLang = urlParams.get('lang');
    const validLangs = tenantSupportedLangs;
    const storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('languageOverride') : null;
    let languageOverridden = false;

    if (paramLang && validLangs.includes(paramLang)) {
      setLanguage(paramLang as any);
      try { localStorage.setItem('languageOverride', paramLang); } catch (e) { /* ignore */ }
      // @ts-ignore
      (window as any).__APP_LANG_STORED__ = paramLang;
      languageOverridden = true;
    }

    // Omnichannel Environment Detection
    const isWeChatBrowser = typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent);
    const isLineBrowser = typeof navigator !== 'undefined' && /Line\//i.test(navigator.userAgent);
    const isNativeLiff = typeof window !== 'undefined' && (window.location.hostname.includes('liff.line.me') || urlParams.has('liff.state'));

    // Channel Detection (survives payment/Stripe redirects via storage)
    const paramChannel = urlParams.get('channel')?.toLowerCase();

    // Determine runtime default channel
    let detectedDefaultChannel = 'web';
    if (isWeChatBrowser || urlParams.has('openid') || urlParams.has('wechat')) {
      detectedDefaultChannel = 'wechat';
    } else if (urlParams.has('phone')) {
      detectedDefaultChannel = 'whatsapp';
    } else if (isNativeLiff || isLineBrowser) {
      detectedDefaultChannel = 'line';
    } else {
      detectedDefaultChannel = 'web';
    }

    const storedRawChannel = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null;

    let activeChannel = paramChannel || (isWeChatBrowser ? 'wechat' : (storedRawChannel || detectedDefaultChannel));

    // If not running in LINE environment, do not force 'line' channel
    if (activeChannel === 'line' && !isLineBrowser && !isNativeLiff) {
      activeChannel = 'web';
    }

    try {
      sessionStorage.setItem('_channel', activeChannel);
      localStorage.setItem('_channel', activeChannel);
    } catch (_) {}

    if (urlParams.get('phone')) {
      try {
        sessionStorage.setItem('_phone', urlParams.get('phone')!);
        localStorage.setItem('_phone', urlParams.get('phone')!);
      } catch (_) {}
    }
    if (urlParams.get('openid')) {
      try {
        sessionStorage.setItem('_openid', urlParams.get('openid')!);
        localStorage.setItem('_openid', urlParams.get('openid')!);
      } catch (_) {}
    }

    // Exchange WeChat OAuth code for openid if present
    const oauthCode = urlParams.get('code');
    if (oauthCode && (activeChannel === 'wechat' || paramChannel === 'wechat')) {
      const tid = urlParams.get('tenant') || (typeof localStorage !== 'undefined' ? localStorage.getItem('_tenant') : null) || 'bandara-beach-resort-phuket';
      fetch(`https://revio-api-6331563496.asia-southeast1.run.app/channel/wechat/oauth/exchange/${tid}?code=${oauthCode}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.openid) {
            try {
              sessionStorage.setItem('_openid', data.openid);
              localStorage.setItem('_openid', data.openid);
              console.log('[WeChat] OpenID successfully captured via OAuth:', data.openid);
            } catch (_) {}
          }
        })
        .catch(() => {});
    }

    const isLine = activeChannel === 'line';
    const isWhatsApp = activeChannel === 'whatsapp';
    const isWeChat = activeChannel === 'wechat';
    const isWeb = activeChannel === 'web';

    // Auto-set language to Chinese (zh) for WeChat if not explicitly overridden
    if (isWeChat && !paramLang && !storedLang) {
      setLanguage('zh' as any);
      try { localStorage.setItem('languageOverride', 'zh'); } catch (_) {}
      (window as any).__APP_LANG_STORED__ = 'zh';
      languageOverridden = true;
    }

    // Early intercept: if Stripe redirected here with redirect_status
    const redirectStatus = urlParams.get('redirect_status');
    if (redirectStatus) {
      setLoading(false);
      const root = document.getElementById('app');
      if (root) {
        const isSuccess = redirectStatus === 'succeeded';
        const returnChatUrl = isWhatsApp ? 'https://wa.me/15556643267' : null;
        root.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;background:#f9f9f9;">
            <div style="font-size:64px;margin-bottom:16px;">${isSuccess ? '✅' : '❌'}</div>
            <h2 style="color:${isSuccess ? '#00B900' : '#cf1322'};margin-bottom:8px;font-size:20px;">
              ${isSuccess ? (t('successTitle') || 'ชำระเงินสำเร็จ') : (t('errorBookingFailed') || 'การชำระเงินไม่สำเร็จ')}
            </h2>
            <p style="color:#666;margin-bottom:24px;font-size:14px;">${isWhatsApp ? t('returnToWhatsAppChat') : isWeChat ? (t('returnToWeChatChat') || '预订已确认，请点击下方按钮关闭页面返回微信。') : isWeb ? t('returnToChatWeb') : t('confirmationSentToChat')}</p>
            <button onclick="${returnChatUrl ? `window.location.href='${returnChatUrl}'` : 'window.close()'}" style="padding:12px 32px;background:${isSuccess ? '#00B900' : '#cf1322'};color:white;border:none;border-radius:8px;font-size:16px;cursor:pointer;">${isWhatsApp ? `💬 ${t('returnToWhatsAppBtn')}` : t('closeWindowBtn')}</button>
          </div>`;
      }
      return;
    }
    let profile: any;

    if (!isLine) {
      // Universal Standalone / Non-LINE mode (WhatsApp, WeChat, Web, etc.): dynamic user profile without LINE LIFF redirect
      const phoneParam = (urlParams.get('phone') || (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_phone') || localStorage.getItem('_phone')) : '') || '').trim();
      const openidParam = (urlParams.get('openid') || (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_openid') || localStorage.getItem('_openid')) : '') || '').trim();
      const nameParam = (urlParams.get('name') || '').trim();
      const userLang = paramLang || (isWeChat ? 'zh' : (navigator.language?.startsWith('zh') ? 'zh' : navigator.language?.startsWith('en') ? 'en' : 'th'));

      let persistentWebId = typeof localStorage !== 'undefined' ? localStorage.getItem('revio_web_user_id') : null;
      if (!persistentWebId) {
        persistentWebId = `web_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        try { localStorage.setItem('revio_web_user_id', persistentWebId); } catch (_) {}
      }

      const savedGuest = loadSavedGuestInfo();
      const derivedDisplayName = nameParam || (savedGuest?.firstName ? `${savedGuest.firstName} ${savedGuest.lastName || ''}`.trim() : (phoneParam ? `+${phoneParam}` : (openidParam ? `WeChat Guest` : '')));

      let userId = persistentWebId;
      if (isWhatsApp && phoneParam) {
        userId = `wa_${phoneParam}`;
      } else if (isWeChat && openidParam) {
        userId = `wechat_${openidParam}`;
      }

      profile = {
        userId,
        displayName: derivedDisplayName,
        pictureUrl: undefined,
        language: userLang,
      };

      setUserProfile(profile);

      if (phoneParam || nameParam || openidParam) {
        setGuestInfo({
          phone: phoneParam || (savedGuest?.phone || ''),
          firstName: nameParam || (savedGuest?.firstName || ''),
          lastName: savedGuest?.lastName || '',
          email: savedGuest?.email || '',
          title: savedGuest?.title,
          nationality: savedGuest?.nationality || (isWeChat ? 'CN' : undefined),
          idType: savedGuest?.idType,
          idNumber: savedGuest?.idNumber,
        });
      } else if (savedGuest && (savedGuest.firstName || savedGuest.phone)) {
        setGuestInfo({
          firstName: savedGuest.firstName || '',
          lastName: savedGuest.lastName || '',
          email: savedGuest.email || '',
          phone: savedGuest.phone || '',
          title: savedGuest.title,
          nationality: savedGuest.nationality,
          idType: savedGuest.idType,
          idNumber: savedGuest.idNumber,
        });

        // If phone is known but first name is empty, auto-fetch full profile from past bookings
        if (savedGuest.phone && !savedGuest.firstName) {
          getApiUserProfile(savedGuest.phone)
            .then((fetchedProfile) => {
              if (fetchedProfile && (fetchedProfile.firstName || fetchedProfile.email)) {
                const cur = loadSavedGuestInfo() || {};
                const merged = {
                  ...cur,
                  firstName: cur.firstName || fetchedProfile.firstName || '',
                  lastName: cur.lastName || fetchedProfile.lastName || '',
                  email: cur.email || fetchedProfile.email || '',
                  phone: cur.phone || fetchedProfile.phone || savedGuest.phone,
                  title: (cur.title || fetchedProfile.title) as any,
                  nationality: cur.nationality || fetchedProfile.nationality,
                  idType: (cur.idType || fetchedProfile.idType) as any,
                  idNumber: cur.idNumber || fetchedProfile.idNumber,
                };
                saveGuestInfoToStorage(merged as any);
                setGuestInfo(merged as any);
              }
            })
            .catch(() => {});
        }
      }
    } else {
      // LINE OA Mode (LIFF flow with graceful room-service bypass)
      await initLiff();

      // Check login status (support E2E mode to avoid redirect)
      const skipLogin = urlParams.get('e2e') === '1' || urlParams.get('e2e') === 'true';
      if (!skipLogin) {
        if (!isLoggedIn()) {
          // If accessing room-service or onboard page, DO NOT force OAuth redirect
          // Allow instant access in external browser without getting 400 Bad Request on LINE redirect_uri
          if (isRoomServicePage) {
            console.log('[RoomService] Instant guest access in LINE browser for room:', roomParam);
          } else if (page === 'onboard') {
            console.log('[Onboard] Hotel registration access');
            if (isInLineApp()) {
              login();
              return;
            }
          } else {
            login();
            return;
          }
        }
      }

      // Get user profile (if fetching fails or unauthenticated, fall back gracefully)
      try {
        if (isLoggedIn()) {
          profile = await getUserProfile();
        } else {
          profile = {
            userId: `line_guest_${Date.now()}`,
            displayName: roomParam ? `Room ${roomParam}` : 'LINE Guest',
            pictureUrl: undefined,
            language: navigator.language || 'th',
          } as any;
        }
      } catch (err) {
        profile = {
          userId: 'e2e-user',
          displayName: roomParam ? `Room ${roomParam}` : 'LINE Guest',
          pictureUrl: undefined,
          language: navigator.language || 'th',
        } as any;
      }
      setUserProfile(profile);
    }

    // If language not overridden by query param, use stored override or LIFF/browser detection
    // Expose detected profile language for debugging / E2E
    // @ts-ignore
    (window as any).__APP_DETECTED_LANG__ = profile.language;

    if (!languageOverridden) {
      if (storedLang && validLangs.includes(storedLang)) {
        setLanguage(storedLang as any);
        // @ts-ignore
        (window as any).__APP_LANG_SOURCE__ = 'stored';
      } else {
        const detected = detectLanguage(profile.language);
        const resolvedLang = validLangs.includes(detected) ? detected : tenantDefaultLang;
        setLanguage(resolvedLang as any);
        // @ts-ignore
        (window as any).__APP_LANG_SOURCE__ = validLangs.includes(detected) ? 'detected' : 'default';
        // @ts-ignore
        (window as any).__APP_DETECTED = resolvedLang;
      }
    } else {
      // @ts-ignore
      (window as any).__APP_LANG_SOURCE__ = 'param';
    }

    // Set user in state
    setUser(profile);

    // Fetch Active Services Config, Hotel Info, and Dynamic MiniApp Config
    const { fetchActiveServicesConfig, getHotelInfo } = await import('./api');
    const { fetchMiniAppConfig } = await import('./miniapp-config');
    const [activeServices, hotelInfo, miniappConfigData] = await Promise.all([
      fetchActiveServicesConfig(),
      getHotelInfo().catch(() => null),
      fetchMiniAppConfig().catch(() => null),
    ]);
    if (hotelInfo?.hotelName) {
      document.title = hotelInfo.hotelName.trim();
    }

    // Expose window.switchService for dynamic service tabs
    (window as any).switchService = (serviceId: string) => {
      const configServices = miniappConfigData?.config?.services || [];
      const targetSvc = configServices.find((s) => s.id === serviceId);
      if (targetSvc) {
        if (targetSvc.engine === 'reservation') {
          navigateTo('search');
        } else if (targetSvc.id === 'spa' && targetSvc.outletType === 'spa') {
          navigateTo('spa');
        } else if (targetSvc.id === 'restaurant' && targetSvc.outletType === 'restaurant') {
          (window as any).resetRestOutlet?.();
          navigateTo('restaurant');
        } else if (targetSvc.id === 'mice' && targetSvc.outletType === 'mice') {
          navigateTo('mice');
        } else if (targetSvc.id === 'room_service' || targetSvc.engine === 'service_ticket') {
          navigateTo('room-service');
        } else {
          setState({ activeServiceId: targetSvc.id });
          navigateTo('service');
        }
      }
    };

    const pendingPayment = localStorage.getItem('stripe_return_booking');

    const serviceParam = (urlParams.get('service') || '').toLowerCase().trim();

    if (pendingPayment) {
      navigateTo('payment');
    } else if (page === 'onboard') {
      await fetchFreeTierStatus().catch(() => null);
      navigateTo('onboard');
    } else if (page === 'my-bookings') {
      navigateTo('my-bookings');
    } else if (isRoomServicePage) {
      const { loadRoomServiceConfigAsync, setRoomServiceActiveTab } = await import('./pages/room-service');
      await loadRoomServiceConfigAsync();

      if (
        tabParam === 'engineer' ||
        tabParam === 'maintenance' ||
        tabParam === 'repair' ||
        tabParam === 'maint' ||
        page === 'engineer' ||
        page === 'maintenance' ||
        page === 'repair'
      ) {
        setRoomServiceActiveTab('maintenance');
      } else if (
        tabParam === 'myrequest' ||
        tabParam === 'myrequests' ||
        tabParam === 'requests' ||
        tabParam === 'history' ||
        page === 'my-requests' ||
        page === 'my-request'
      ) {
        setRoomServiceActiveTab('my-requests');
      } else if (
        tabParam === 'hk' ||
        tabParam === 'housekeeping' ||
        page === 'hk' ||
        page === 'housekeeping'
      ) {
        setRoomServiceActiveTab('housekeeping');
      }

      navigateTo('room-service');
    } else if (page === 'room-types') {
      navigateTo('room-types');
    } else if (page === 'restaurant' || urlParams.get('mode') === 'restaurant' || urlParams.has('table')) {
      navigateTo('restaurant');
    } else if (page === 'spa' && activeServices.spa) {
      navigateTo('spa');
    } else if (page === 'mice' && activeServices.mice) {
      navigateTo('mice');
    } else if (page === 'service') {
      const configServices = miniappConfigData?.config?.services || [];
      const currentServiceId = getState().activeServiceId || serviceParam || miniappConfigData?.config?.defaultServiceId;
      const targetSvc = configServices.find((s) => s.id === currentServiceId) || configServices[0];
      if (targetSvc && typeof (window as any).switchService === 'function') {
        (window as any).switchService(targetSvc.id);
      } else {
        navigateTo('service');
      }
    } else if (serviceParam) {
      const configServices = miniappConfigData?.config?.services || [];
      const match = configServices.find((s) => s.id === serviceParam);
      if (match) {
        (window as any).switchService(match.id);
      } else {
        navigateTo('search');
      }
    } else {
      // Dynamic routing using config.defaultServiceId if present
      const configServices = miniappConfigData?.config?.services || [];
      const defaultSvcId = miniappConfigData?.config?.defaultServiceId;
      const defaultSvc = configServices.find((s) => s.id === defaultSvcId) || configServices[0];

      if (defaultSvc && defaultSvc.engine !== 'reservation') {
        (window as any).switchService(defaultSvc.id);
      } else {
        // Auto-route to first active service tab (legacy hotel fallback)
        if (activeServices.rooms) {
          navigateTo('search');
        } else if (activeServices.spa) {
          navigateTo('spa');
        } else if (activeServices.restaurant) {
          navigateTo('restaurant');
        } else if (activeServices.mice) {
          navigateTo('mice');
        } else {
          navigateTo('search');
        }
      }
    }

    // Expose current language getter for E2E tests
    // @ts-ignore
    (window as any).__APP_GET_LANGUAGE__ = getCurrentLanguage;
    // @ts-ignore
    (window as any).__APP_CURRENT_LANG__ = getCurrentLanguage();

    console.log('App initialized:', {
      user: profile.displayName,
      language: getCurrentLanguage(),
      inLineApp: isInLineApp(),
      page: page || 'booking (default)',
    });
  } catch (error) {
    console.error('Failed to initialize app:', error);
    setError(t('errorGeneric'));
  } finally {
    setLoading(false);
  }
}

// Subscribe to state changes and re-render
subscribe(() => {
  render();
});

// Helper: Save current form data before re-render
function saveGuestFormData(): void {
  const form = document.getElementById('guest-form') as HTMLFormElement;
  if (form) {
    const formData = new FormData(form);
    setGuestInfo({
      firstName: (formData.get('firstName') as string)?.trim() || '',
      lastName: (formData.get('lastName') as string)?.trim() || '',
      email: (formData.get('email') as string)?.trim() || '',
      phone: (formData.get('phone') as string)?.trim() || '',
      specialRequests: (formData.get('specialRequests') as string)?.trim() || undefined,
    });
  }
}

// Modal open/close handlers
window.openLangModal = () => {
  const overlay = document.getElementById('lang-modal-overlay');
  if (overlay) {
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
};

window.closeLangModal = () => {
  const overlay = document.getElementById('lang-modal-overlay');
  if (overlay) {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
  }
};

// Global language switcher handlers
window.switchLanguage = async (lang: Language) => {
  // Save form data before switching language (if on guest-info or room-service page)
  const state = getState();
  if (state.currentPage === 'guest-info') {
    saveGuestFormData();
  } else if (state.currentPage === 'room-service') {
    saveRoomServiceInputsFromDom();
  }

  setLanguage(lang);
  try { localStorage.setItem('languageOverride', lang); } catch (e) { /* ignore */ }
  import('./api').then(({ getHotelInfo }) => {
    getHotelInfo().then((info) => {
      if (info?.hotelName) document.title = info.hotelName.trim();
    }).catch(() => {});
  });
  // Expose stored value for E2E
  // @ts-ignore
  (window as any).__APP_LANG_STORED__ = lang;
  // Close language modal
  window.closeLangModal?.();

  // If on rooms or confirm page, we should re-fetch room search results to get descriptions in the new language
  if ((state.currentPage === 'rooms' || state.currentPage === 'confirm') && state.search) {
    const { searchRooms } = await import('./api');
    
    // Set loading state in the main store so spinner shows up
    setState({ loading: true });
    
    try {
      const freshRooms = await searchRooms(state.search);
      
      // Update store with fresh rooms (which now have new language descriptions)
      // If a room was already selected, update it too
      let updatedSelectedRoom: import('./types').RoomAvailability | null = null;
      if (state.selectedRoom) {
        updatedSelectedRoom = freshRooms.find(
          r => r.roomTypeCode === state.selectedRoom!.roomTypeCode && 
               r.ratePlanCode === state.selectedRoom!.ratePlanCode
        ) || null;
      }
      
      setState({
        availableRooms: freshRooms,
        selectedRoom: updatedSelectedRoom,
        loading: false,
      });
    } catch (err) {
      console.error('[LanguageSwitch] Failed to refresh availability on language change:', err);
      setState({ loading: false });
    }
  } else if (state.currentPage === 'room-types') {
    // If on room-types page, re-run initialization to fetch data in new language
    const { initRoomTypesPage } = await import('./pages/room-types');
    // initRoomTypesPage handles loading state and rendering automatically
    initRoomTypesPage();
    return;
  }

  render(); // Re-render with new language
};

// Global navigation handlers
(window as any).navigateTo = (page: any) => {
  navigateTo(page);
};
window.goHome = () => {
  const cachedCfg = getCachedMiniAppConfig();
  const configServices = cachedCfg?.config?.services || [];
  const defaultSvcId = cachedCfg?.config?.defaultServiceId;
  const defaultSvc = configServices.find((s: any) => s.id === defaultSvcId) || configServices[0];

  if (defaultSvc && defaultSvc.engine !== 'reservation') {
    if (typeof (window as any).switchService === 'function') {
      (window as any).switchService(defaultSvc.id);
    } else {
      setState({ activeServiceId: defaultSvc.id });
      navigateTo('service');
    }
  } else {
    navigateTo('search');
  }
};

// Close modal on Escape key
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    window.closeLangModal?.();
  }
});

// Start the app
initApp();

// Extend window for global handlers
declare global {
  interface Window {
    handleRetry?: () => void;
    switchLanguage?: (lang: Language) => void;
    openLangModal?: () => void;
    closeLangModal?: () => void;
    toggleLangDropdown?: () => void;
    goHome?: () => void;
  }
}
